<template>
  <section class="card-style rounded-xl p-4 sm:p-6 group hover:shadow-lg hover:shadow-blue-500/5 transition-all">
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-medium text-white flex items-center gap-2">
        <div class="p-1.5 rounded-lg bg-orange-500/10">
          <Icon 
            icon="heroicons:question-mark-circle" 
            class="w-5 h-5 text-orange-400"
            aria-hidden="true"
          />
        </div>
        Frequently Asked Questions
      </h2>
    </div>
    <div v-if="hasFaqs" class="space-y-4 mb-4">
      <div 
        v-for="(faq, index) in tool.published_faqs" 
        :key="faq.id"
        class="border-0 border-gray-700/30 rounded-lg overflow-hidden transition-all"
        :class="{ 'shadow-sm shadow-blue-500/5': expandedFaq === index }"
      >
        <button 
          @click="toggleFaq(index)" 
          class="w-full flex items-center justify-between p-3 text-left focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-opacity-40 rounded-lg"
          :class="{ 
            'bg-gray-800/70': expandedFaq === index,
            'bg-gray-800/30 hover:bg-gray-800/50': expandedFaq !== index
          }"
          :aria-expanded="expandedFaq === index"
          :aria-controls="`faq-content-${index}`"
        >
          <div class="flex items-center gap-3">
            <div class="bg-gray-700/50 rounded-full p-1.5 flex-shrink-0">
              <Icon 
                icon="heroicons:question-mark" 
                class="w-3.5 h-3.5 text-blue-400"
                aria-hidden="true"
              />
            </div>
            <h3 class="text-sm font-medium text-white pr-8">{{ faq.question }}</h3>
          </div>
          <div 
            class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors"
            :class="{ 'bg-blue-500/10': expandedFaq === index }"
          >
            <Icon 
              :icon="expandedFaq === index ? 'heroicons:minus-small' : 'heroicons:plus-small'" 
              class="w-5 h-5"
              :class="expandedFaq === index ? 'text-blue-400' : 'text-gray-400'"
              aria-hidden="true"
            />
          </div>
        </button>
        
        <div 
          v-show="expandedFaq === index" 
          :id="`faq-content-${index}`"
          class="p-4 bg-gray-800/10 text-sm text-gray-300 leading-relaxed rounded-b-lg"
          v-html="sanitizeHtml(faq.answer)"
        ></div>
      </div>
    </div>
    
    <!-- Empty state when no FAQs are available -->
    <div v-else class="p-4 text-center bg-gray-800/30 rounded-lg">
      <Icon icon="heroicons:information-circle" class="w-8 h-8 text-gray-500 mx-auto mb-2" aria-hidden="true" />
      <p class="text-gray-400 text-sm">No frequently asked questions are available for this tool.</p>
    </div>
    <!-- Hidden but SEO-friendly content -->
    <div class="sr-only">
      <h3>FAQs about {{ tool.name }}:</h3>
      <dl v-if="hasFaqs">
        <template v-for="faq in tool.published_faqs" :key="faq.id">
          <dt>{{ faq.question }}</dt>
          <dd>{{ stripHtml(faq.answer) }}</dd>
        </template>
      </dl>
      <p v-else>No FAQs are currently available for {{ tool.name }}.</p>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Icon } from '@iconify/vue';
import DOMPurify from 'dompurify';

const props = defineProps({
  tool: {
    type: Object,
    required: true,
    validator: (tool) => {
      return tool.id && typeof tool.name === 'string';
    }
  }
});

// Track which FAQ is currently expanded
const expandedFaq = ref(null);

// Toggle FAQ expansion with smooth animation effect
const toggleFaq = (index) => {
  if (expandedFaq.value === index) {
    // Add small delay to allow for animation when closing
    setTimeout(() => {
      expandedFaq.value = null;
    }, 50);
  } else {
    expandedFaq.value = index;
  }
};

// Check if the tool has any FAQs
const hasFaqs = computed(() => {
  return props.tool.published_faqs && props.tool.published_faqs.length > 0;
});

// Sanitize HTML content for security
const sanitizeHtml = (html) => {
  return DOMPurify.sanitize(html);
};

// Strip HTML for screen readers and SEO
const stripHtml = (html) => {
  return html.replace(/<[^>]*>/g, '');
};
</script>

<style scoped>
.card-style {
  background: radial-gradient(
    ellipse at center,
    rgba(30, 41, 59, 0.5) 0%,
    rgba(17, 24, 39, 0.7) 100%
  );
  backdrop-filter: blur(2px);
}

/* Add subtle hover effect to each FAQ item */
button {
  transition: background-color 0.2s ease, color 0.2s ease;
}

/* Smoother transitions for the expanded/collapsed states */
div[id^="faq-content-"] {
  transition: max-height 0.3s ease, opacity 0.2s ease, padding 0.2s ease;
}
</style>